import React, { useMemo } from 'react'
import { Box, ButtonBase, FormControlLabel, MenuItem, Switch, TextField, Typography } from '@material-ui/core'
import { alpha, useTheme } from '@material-ui/core/styles'
import { useQuery } from 'react-query'
import { fetchGatkCohorts, fetchExomeDepthCohorts } from 'src/api/analysis/cohort'
import { useRnaVocabulary } from 'src/api/rna'
import { rnaAvailability } from 'src/views/AnalysisView/lib/rnaLibrary'
import { GroupLabel, Muted, ErrorLine, TextLink, Segmented, KindIcon } from './components/ui'
import TumourTypeSelect from './components/TumourTypeSelect'
import KitSelect, { kitMenuOptions } from './components/KitSelect'
import LongReadOptions from './components/LongReadOptions'
import GroupColumnOptions from './groups/GroupColumnOptions'
import { FEATURES } from './model/features'
import {
  ANALYSIS_OPTIONS,
  GENESUS_AUTOMATIC_COHORT,
  INPUT_HINT,
  INPUT_OPTIONS,
  SEQ_OPTIONS,
  MODE_OPTIONS,
  seqApplies,
  CNV_METHODS,
  REFERENCE_OPTIONS,
  needsKit,
  cnvApplies,
  longReadApplies,
  referenceApplies,
  modeApplies,
  tumourTypeApplies,
  aiApplies,
  isFastq,
  isGroupAnalysis,
  cohortMatchesKit,
} from './model/workflow'

// The left column of the panel: the workflow, set once for every sample. Only the
// groups that apply render, separated by space rather than lines. Controls are at
// most 352px wide. A choice that must be made reads "Required" until it is made,
// and turns into the red reason once a blocked Review has been tried.
//
// Every analysis is a tile. Germline and Somatic run on files (Input and the file
// options below); Multisample, CNV cohort and Carrier run on groups of finished
// analyses, with their options from groups/GroupColumnOptions.

const COLUMN_CONTROL_WIDTH = 352

const AnalysisTile = ({ tile, selected, onClick }) => {
  const theme = useTheme()
  const accent = tile.value === 'somatic' ? theme.palette.somatic.main : theme.palette.primary.main
  // Group analyses have quiet icons, coloured once chosen.
  const quietIcon = tile.value !== 'germline' && tile.value !== 'somatic'
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={selected}
      sx={{
        minWidth: 0,
        height: 56,
        px: 1.5,
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 1.25,
        textAlign: 'left',
        borderRadius: `${theme.shape.borderRadius}px`,
        border: `1px solid ${selected ? accent : theme.palette.divider}`,
        boxShadow: selected ? `inset 0 0 0 1px ${accent}` : 'none',
        transition: theme.transitions.create(['border-color', 'box-shadow'], { duration: 120 }),
        '&:hover': { borderColor: selected ? accent : alpha(theme.palette.text.primary, 0.32) },
        '&.Mui-focusVisible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
      }}
    >
      <KindIcon kind={tile.value} sx={quietIcon && selected ? { color: accent } : undefined} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, lineHeight: 1.3 }} noWrap>
          {tile.title}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.3 }} noWrap>
          {tile.hint}
        </Typography>
      </Box>
    </ButtonBase>
  )
}

const Group = ({ label, children }) => (
  <Box>
    {label && <GroupLabel>{label}</GroupLabel>}
    {children}
  </Box>
)

const selectSx = { width: '100%', maxWidth: COLUMN_CONTROL_WIDTH, '& .MuiInputBase-root': { fontSize: 14.5 } }

// Under a required choice that is not made yet: muted, or the reason in red after a
// blocked click.
const RequiredHint = ({ error, showErrors }) => {
  if (!error) return null
  return showErrors ? <ErrorLine>{error}</ErrorLine> : <Muted sx={{ mt: 0.75 }}>Required</Muted>
}

const cohortStatusText = (status) => {
  if (status === 'COMPLETED') return ''
  if (status === 'FAILED') return 'Failed'
  if (!status) return 'In progress'
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

// The cohort list of a CNV method; the panel reads the same query to check a
// remembered cohort.
export const cnvCohortsQueryKey = (method) => ['new-analysis', 'cnv-cohorts', method]
export const fetchCnvCohorts = (method) => (method === 'exomedepth' ? fetchExomeDepthCohorts() : fetchGatkCohorts())

const CnvOptions = ({ workflow, onChange, error }) => {
  const method = workflow.cnvMethod
  const usesCohort = method === 'genesus-cnv' || method === 'exomedepth'
  const { data, isLoading } = useQuery(cnvCohortsQueryKey(method), () => fetchCnvCohorts(method), {
    enabled: workflow.cnv && usesCohort,
    staleTime: 60 * 1000,
  })
  const cohorts = useMemo(() => {
    const list = Array.isArray(data?.cohorts) ? data.cohorts : []
    const defaultId = data?.default_cohort?.id
    return list
      .filter((c) => c && c.id && cohortMatchesKit(c, workflow.kitId))
      .filter((c) => method !== 'genesus-cnv' || c.id !== defaultId)
  }, [data, workflow.kitId, method])

  const setMethod = (next) =>
    onChange({ cnvMethod: next, cnvCohortId: next === 'exomedepth' ? '' : GENESUS_AUTOMATIC_COHORT })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5, maxWidth: COLUMN_CONTROL_WIDTH }}>
      <TextField
        select
        size="small"
        label="Method"
        value={method}
        onChange={(e) => setMethod(e.target.value)}
        SelectProps={{ renderValue: (value) => CNV_METHODS.find((m) => m.value === value)?.label || value }}
        sx={selectSx}
      >
        {CNV_METHODS.map((m) => (
          <MenuItem key={m.value} value={m.value} sx={{ fontSize: 14.5, gap: 1 }}>
            {m.label}
            {m.meta && (
              <Typography component="span" sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                {m.meta}
              </Typography>
            )}
          </MenuItem>
        ))}
      </TextField>
      {usesCohort && (
        <Box>
          <TextField
            select
            size="small"
            label="Cohort"
            value={workflow.cnvCohortId || ''}
            onChange={(e) => onChange({ cnvCohortId: e.target.value })}
            disabled={isLoading}
            error={!!error}
            sx={selectSx}
            SelectProps={{ displayEmpty: method === 'exomedepth' }}
            InputLabelProps={method === 'exomedepth' ? { shrink: true } : undefined}
          >
            {method === 'genesus-cnv' && (
              <MenuItem value={GENESUS_AUTOMATIC_COHORT} sx={{ fontSize: 14.5 }}>
                Genesus automatic
              </MenuItem>
            )}
            {method === 'exomedepth' && (
              <MenuItem value="" disabled sx={{ fontSize: 14.5 }}>
                {cohorts.length ? 'Choose a finished cohort' : 'No cohort for this kit'}
              </MenuItem>
            )}
            {cohorts.map((c) => (
              <MenuItem
                key={c.id}
                value={c.id}
                disabled={c.status !== 'COMPLETED'}
                sx={{ fontSize: 14.5, gap: 1, justifyContent: 'space-between' }}
              >
                <span>{c.cohort_name}</span>
                <Typography component="span" sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                  {cohortStatusText(c.status)}
                </Typography>
              </MenuItem>
            ))}
          </TextField>
          {error ? (
            <ErrorLine>{error}</ErrorLine>
          ) : (
            method === 'genesus-cnv' &&
            workflow.cnvCohortId === GENESUS_AUTOMATIC_COHORT && (
              <Muted sx={{ mt: 0.75 }}>Uses samples on the same kit as a reference.</Muted>
            )
          )}
        </Box>
      )}
    </Box>
  )
}

const SwitchRow = ({ label, checked, onChange, disabled, hint }) => (
  <Box>
    <FormControlLabel
      control={<Switch size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} />}
      disabled={disabled}
      label={<Typography sx={{ fontSize: 14.5 }}>{label}</Typography>}
      sx={{ ml: 0, gap: 1, '& .MuiSwitch-root': { ml: -0.5 } }}
    />
    {hint && <Muted sx={{ ml: 5.25, mt: -0.25 }}>{hint}</Muted>}
  </Box>
)

// "PacBio HiFi long reads. 7 waiting." Somatic VCFs are SNV/Indel only.
export const inputHint = (workflow, waitingByInput = {}) => {
  const describe =
    workflow.analysis === 'somatic' && workflow.input === 'vcf' ? 'SNV/Indel calls' : INPUT_HINT[workflow.input]
  const waiting = Number(waitingByInput[workflow.input]) || 0
  return [describe && `${describe}.`, waiting ? `${waiting} waiting.` : ''].filter(Boolean).join(' ')
}

// Whether a tumour RNA analysis can start in this environment, said plainly.
// While unknown it says nothing: the start itself is authoritative and refuses
// cleanly, before anything is written or charged.
export const RnaAvailabilityNote = () => {
  const { data } = useRnaVocabulary()
  const availability = rnaAvailability(data)
  return (
    <Box sx={{ mt: 0.75 }} data-testid="rna-availability">
      <Muted>
        {availability.enabled
          ? 'One tumour RNA FASTQ pair per sample. Library details are entered per sample.'
          : availability.reason}
      </Muted>
    </Box>
  )
}

const WorkflowColumn = ({
  workflow,
  onChange,
  samples,
  sampleCount,
  bedFiles = [],
  errors = {},
  showErrors,
  rememberedHint = false,
  priceText,
  onOpenOther,
  waitingByInput = {},
  longReadStats,
}) => {
  const set = (patch) => onChange(patch)
  const count = Number.isFinite(sampleCount) ? sampleCount : samples.length
  const showAi = aiApplies(workflow, samples)
  const groupMode = isGroupAnalysis(workflow)

  const kitOptions = useMemo(() => kitMenuOptions(bedFiles), [bedFiles])

  const setKit = (value) => {
    const kit = kitOptions.find((k) => k.value === value)
    set({
      kitId: value,
      kitName: kit?.label || '',
      cnvCohortId: workflow.cnvMethod === 'exomedepth' ? '' : GENESUS_AUTOMATIC_COHORT,
    })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 2 }}>
      <Group label="Analysis">
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, maxWidth: COLUMN_CONTROL_WIDTH }}>
          {ANALYSIS_OPTIONS.map((tile) => (
            <AnalysisTile
              key={tile.value}
              tile={tile}
              selected={workflow.analysis === tile.value}
              onClick={() => {
                if (workflow.analysis !== tile.value) set({ analysis: tile.value })
              }}
            />
          ))}
        </Box>
        {rememberedHint && <Muted sx={{ mt: 0.75 }}>Same as your last analysis.</Muted>}
        {FEATURES.otherAnalysisTypesLink && onOpenOther && (
          <Box sx={{ mt: 1.25 }}>
            <TextLink onClick={onOpenOther}>Other analysis types</TextLink>
          </Box>
        )}
      </Group>

      {groupMode && (
        <GroupColumnOptions
          workflow={workflow}
          onChange={set}
          errors={errors}
          showErrors={showErrors}
          bedFiles={bedFiles}
        />
      )}

      {!groupMode && (
        <Group label="Input">
          <Box sx={{ maxWidth: COLUMN_CONTROL_WIDTH }}>
            <Segmented
              ariaLabel="Input"
              value={workflow.input}
              options={INPUT_OPTIONS[workflow.analysis] || INPUT_OPTIONS.germline}
              onChange={(input) => set({ input })}
            />
          </Box>
          <Muted sx={{ mt: 0.75 }}>{inputHint(workflow, waitingByInput)}</Muted>
        </Group>
      )}

      {!groupMode && modeApplies(workflow) && (
        <Group label="Mode">
          <Box sx={{ maxWidth: COLUMN_CONTROL_WIDTH }}>
            <Segmented
              ariaLabel="Mode"
              value={workflow.mode}
              options={MODE_OPTIONS}
              onChange={(mode) => set({ mode })}
            />
          </Box>
          {workflow.mode === 'rna' && <RnaAvailabilityNote />}
        </Group>
      )}

      {!groupMode && seqApplies(workflow) && (
        <Group label="Sequencing">
          <Box sx={{ maxWidth: COLUMN_CONTROL_WIDTH }}>
            <Segmented
              ariaLabel="Sequencing"
              value={workflow.seq}
              options={SEQ_OPTIONS}
              onChange={(seq) => set({ seq })}
            />
          </Box>
          <RequiredHint error={errors.seq} showErrors={showErrors} />
        </Group>
      )}

      {!groupMode && needsKit(workflow) && (
        <Group label="Capture kit">
          <KitSelect
            value={workflow.kitId}
            options={kitOptions}
            onChange={setKit}
            error={showErrors && !!errors.kit}
            fallbackLabel={workflow.kitName}
            sx={selectSx}
          />
          <RequiredHint error={errors.kit} showErrors={showErrors} />
          <Box sx={{ mt: 0.75 }}>
            <TextLink component="a" href="/libra/kits" target="_blank" rel="noopener">
              Add a kit
            </TextLink>
          </Box>
        </Group>
      )}

      {!groupMode && cnvApplies(workflow) && (
        <Group label="CNV">
          <SwitchRow label="CNV calling" checked={!!workflow.cnv} onChange={(cnv) => set({ cnv })} />
          {workflow.cnv && <CnvOptions workflow={workflow} onChange={set} error={errors.cohort} />}
          {workflow.kitId && (
            <Box sx={{ mt: 1.25 }}>
              <TextLink
                onClick={() =>
                  // The kit carries over (switchWorkflow shares it with the cohort).
                  set({
                    analysis: 'cohort',
                    cohortMethod: workflow.cnvMethod === 'exomedepth' ? 'exomedepth' : 'genesus-cnv',
                  })
                }
              >
                Build a cohort for this kit
              </TextLink>
            </Box>
          )}
        </Group>
      )}

      {!groupMode && longReadApplies(workflow) && (
        <Group label="Long-read options">
          <LongReadOptions workflow={workflow} onChange={set} stats={longReadStats} />
        </Group>
      )}

      {!groupMode && referenceApplies(workflow) && (
        <Group label="Reference genome">
          <Box sx={{ maxWidth: COLUMN_CONTROL_WIDTH }}>
            <Segmented
              ariaLabel="Reference genome"
              value={workflow.reference === 'None' ? null : workflow.reference}
              options={REFERENCE_OPTIONS}
              onChange={(reference) => set({ reference })}
            />
          </Box>
          <RequiredHint error={errors.reference} showErrors={showErrors} />
        </Group>
      )}

      {/* One sample has its own tumour type picker; a default is for a batch. */}
      {!groupMode && tumourTypeApplies(workflow) && count >= 2 && (
        <Group label="Default tumour type">
          <TumourTypeSelect
            value={workflow.tumourType}
            onChange={(tumourType) => set({ tumourType })}
            sx={{ maxWidth: COLUMN_CONTROL_WIDTH }}
          />
          <Muted sx={{ mt: 0.75 }}>Can differ per sample. Optional.</Muted>
        </Group>
      )}

      {workflow.analysis === 'germline' && (
        <Group label="Interpretation">
          {showAi ? (
            <>
              <Box sx={{ maxWidth: COLUMN_CONTROL_WIDTH }}>
                <Segmented
                  ariaLabel="Interpretation"
                  value={workflow.interpretation}
                  options={[
                    { value: 'standard', label: 'Standard' },
                    { value: 'ai', label: 'AI-assisted' },
                  ]}
                  onChange={(interpretation) => set({ interpretation })}
                />
              </Box>
              {workflow.interpretation === 'ai' && (
                <Muted sx={{ mt: 0.75 }}>
                  Each AI sample needs HPO terms and clinical history. Can differ per sample.
                </Muted>
              )}
            </>
          ) : (
            <Muted>AI-assisted is not available for CNV and SV VCFs.</Muted>
          )}
        </Group>
      )}

      {!groupMode && priceText && <Muted>{priceText}</Muted>}
    </Box>
  )
}

export default WorkflowColumn
